import { useSyncExternalStore } from "react";

/**
 * Which way the long lists page through their rows — the contracts list and
 * the statement preview — a prototype-only dial,
 * not a product setting. The two variants are being compared side by side, so
 * switching has to be instant and has to start the list over; the dev bar owns
 * the control, the card only reads it.
 */
export type ListBehavior = "pagination" | "load_more";

export const LIST_BEHAVIORS: { id: ListBehavior; label: string }[] = [
  { id: "pagination", label: "Pagination" },
  { id: "load_more", label: "Load more" },
];

const KEY = "brs-list-behavior";
const DEFAULT: ListBehavior = "load_more";

function read(): ListBehavior {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "pagination" || stored === "load_more") return stored;
  } catch {
    /* fall through to the default */
  }
  return DEFAULT;
}

let current: ListBehavior = read();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setListBehavior(next: ListBehavior): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* the choice just doesn't survive a reload */
  }
  for (const listener of listeners) listener();
}

export function useListBehavior(): ListBehavior {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => DEFAULT,
  );
}

export function activeListBehavior(): ListBehavior {
  return current;
}
